import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { AnimatedIcon } from './icons';
import { dark } from '../theme/darkColors';
import { spacing, borderRadius } from '../theme/responsive';

type LocationState = 'checking' | 'allowed' | 'blocked';

export const LocationEnforcer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<LocationState>('checking');
  const [message, setMessage] = useState('');

  const checkLocationStatus = useCallback(() => {
    setState('checking');
    setMessage('');

    if (!navigator.geolocation) {
      setState('blocked');
      setMessage('Location is not available in this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => setState('allowed'),
      () => {
        setState('blocked');
        setMessage('Turn on browser location access to use Survey Connect.');
      },
      {
        enableHighAccuracy: false,
        maximumAge: 60000,
        timeout: 12000,
      },
    );
  }, []);

  useEffect(() => {
    checkLocationStatus();
  }, [checkLocationStatus]);

  if (state === 'allowed') {
    return <>{children}</>;
  }

  if (state === 'checking') {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={dark.brandPrimary} />
        <Text style={styles.loadingText}>Checking location requirements...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.iconCircle}>
        <AnimatedIcon name="location" size={48} color={dark.brandPrimary} />
      </View>
      <Text style={styles.title}>Location Required</Text>
      <Text style={styles.subtitle}>
        {message || 'Survey Connect requires location access to match water and borehole survey jobs nearby.'}
      </Text>
      <TouchableOpacity style={styles.button} onPress={checkLocationStatus} activeOpacity={0.8}>
        <Text style={styles.buttonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: dark.background,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    color: dark.textSecondary,
    fontSize: 14,
    fontWeight: '600',
  },
  iconCircle: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: dark.brandPrimary + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: 22,
    fontWeight: '900',
    color: dark.textPrimary,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: dark.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: spacing.xl,
  },
  button: {
    backgroundColor: dark.brandPrimary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: borderRadius.md,
    width: '100%',
    alignItems: 'center',
    maxWidth: 320,
  },
  buttonText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '800',
  },
});
